// PageLoader.jsx   Divyyog Foundation
// Uses the ACTUAL logo image (logo_12.png) with orange CSS filter + glow
// SVG is used ONLY for the circular progress ring around the image

import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";

/* ─────────────────────────────────────────────
   Progress ring + logo image combined
───────────────────────────────────────────── */
function LogoWithRing({ progress }) {
  const SIZE = 280;
  const CX = 140, CY = 140, R = 126;
  const circ    = 2 * Math.PI * R;
  const dashOff = circ * (1 - progress / 100);

  return (
    <div className="relative" style={{ width: SIZE, height: SIZE }}>

      {/* ── SVG: progress ring + deco rings only ── */}
      <svg
        className="absolute inset-0"
        width={SIZE} height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        fill="none"
      >
        <defs>
          <linearGradient id="pg-gold" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="#F29A0C" />
            <stop offset="50%"  stopColor="#FFD166" />
            <stop offset="100%" stopColor="#E97818" />
          </linearGradient>
        </defs>

        {/* Track */}
        <circle cx={CX} cy={CY} r={R}
          stroke="rgba(242,154,12,0.10)" strokeWidth="1.5" fill="none" />

        {/* Animated gold progress ring */}
        <motion.circle
          cx={CX} cy={CY} r={R}
          stroke="url(#pg-gold)"
          strokeWidth="2.8" fill="none" strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={dashOff}
          style={{
            rotate: -90,
            transformOrigin: `${CX}px ${CY}px`,
            filter: "drop-shadow(0 0 8px rgba(242,154,12,0.60))",
          }}
        />

        {/* Inner dashed ring   counter-clockwise spin */}
        <motion.circle
          cx={CX} cy={CY} r={110}
          stroke="rgba(242,154,12,0.18)" strokeWidth="0.9"
          strokeDasharray="3 11" fill="none"
          style={{ transformOrigin: `${CX}px ${CY}px`, transformBox: "fill-box" }}
          animate={{ rotate: -360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />

        {/* Outer slow-spin dashed ring */}
        <motion.circle
          cx={CX} cy={CY} r={R}
          stroke="rgba(242,154,12,0.06)" strokeWidth="0.7"
          strokeDasharray="1 18" fill="none"
          style={{ transformOrigin: `${CX}px ${CY}px`, transformBox: "fill-box" }}
          animate={{ rotate: 360 }}
          transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
        />
      </svg>

      {/* ── Pulsing glow behind the logo ── */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <motion.div
          animate={{ scale: [0.9, 1.18, 0.9], opacity: [0.28, 0.55, 0.28] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          style={{
            width: 155, height: 155,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(242,154,12,0.55), rgba(233,120,24,0.15) 55%, transparent 70%)",
            filter: "blur(18px)",
          }}
        />
      </div>

      {/* ── Actual logo image   orange tinted ──
           logo_12.png is white-on-dark;
           mix-blend-mode:screen makes the dark bg invisible,
           leaving only the white logo shape   then filter turns it gold  ── */}
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.img
          src="/assets/logo_12.png"
          alt="Divyyog Logo"
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: 1,   opacity: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          style={{
            width: 172, height: "auto",
            objectFit: "contain",
            mixBlendMode: "screen",
            // White → rich gold/amber conversion
            filter:
              "sepia(1) saturate(9) hue-rotate(4deg) brightness(1.25)" +
              " drop-shadow(0 0 22px rgba(242,154,12,0.70))" +
              " drop-shadow(0 0  8px rgba(255,200,60,0.50))",
          }}
        />
      </div>

      {/* ── Progress % label   bottom-centre of ring ── */}
      <div className="absolute inset-0 flex items-end justify-center pb-6 pointer-events-none">
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: 13, fontWeight: 800,
            color: "#F29A0C",
            letterSpacing: "1px",
            filter: "drop-shadow(0 0 6px rgba(242,154,12,0.70))",
          }}
        >
          {progress}%
        </motion.span>
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────
   Floating gold particles
───────────────────────────────────────────── */
function Particles() {
  const pts = [
    { left:"6%",  sz:2.5, dur:2.4, dl:0,    rise:130, cl:"#F29A0C", op:0.55 },
    { left:"14%", sz:1.5, dur:3.1, dl:0.55, rise:105, cl:"#FFD166", op:0.38 },
    { left:"23%", sz:2,   dur:2.7, dl:1.1,  rise:148, cl:"#F29A0C", op:0.45 },
    { left:"34%", sz:1.5, dur:3.4, dl:0.3,  rise:118, cl:"#FFB347", op:0.35 },
    { left:"46%", sz:3,   dur:2.2, dl:0.8,  rise:144, cl:"#F29A0C", op:0.60 },
    { left:"56%", sz:1.5, dur:3.0, dl:0.2,  rise:108, cl:"#FFD166", op:0.38 },
    { left:"66%", sz:2,   dur:2.6, dl:1.35, rise:136, cl:"#F29A0C", op:0.48 },
    { left:"76%", sz:1.5, dur:2.9, dl:0.6,  rise:116, cl:"#FFB347", op:0.38 },
    { left:"84%", sz:2.5, dur:2.3, dl:0.9,  rise:140, cl:"#F29A0C", op:0.55 },
    { left:"92%", sz:1.5, dur:3.2, dl:0.45, rise:104, cl:"#FFD166", op:0.38 },
  ];
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {pts.map((p, i) => (
        <motion.div key={i} className="absolute rounded-full"
          style={{ left:p.left, bottom:"10%", width:p.sz, height:p.sz, background:p.cl, opacity:0 }}
          animate={{ y:[0,-p.rise], opacity:[0,p.op,0], scale:[1,0.5,0] }}
          transition={{ duration:p.dur, repeat:Infinity, delay:p.dl, ease:"easeOut" }}
        />
      ))}
    </div>
  );
}


/* ─────────────────────────────────────────────
   MAIN PAGE LOADER
───────────────────────────────────────────── */
export default function PageLoader({ onComplete }) {
  const [progress, setProgress] = useState(0);
  const [visible,  setVisible]  = useState(true);

  useEffect(() => {
    const checkVisibilityAndComplete = () => {
      if (document.hidden) {
        setProgress(100);
        setVisible(false);
        onComplete?.();
        return true;
      }
      return false;
    };

    if (checkVisibilityAndComplete()) return;

    let fid, t0 = null;
    const DURATION = 2800;

    const tick = (ts) => {
      if (checkVisibilityAndComplete()) return;
      if (!t0) t0 = ts;
      const t = Math.min((ts - t0) / DURATION, 1);
      const e = t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t+2, 3)/2;
      setProgress(Math.round(e * 100));
      if (t < 1) {
        fid = requestAnimationFrame(tick);
      } else {
        setTimeout(() => {
          setVisible(false);
          setTimeout(() => onComplete?.(), 900);
        }, 480);
      }
    };

    const handleVisibility = () => {
      checkVisibilityAndComplete();
    };

    document.addEventListener("visibilitychange", handleVisibility);

    fid = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(fid);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [onComplete]);

  const brackets = [
    { cls:"top-8 left-8",     style:{ borderTop:"1.5px solid", borderLeft:"1.5px solid"   } },
    { cls:"top-8 right-8",    style:{ borderTop:"1.5px solid", borderRight:"1.5px solid"  } },
    { cls:"bottom-8 left-8",  style:{ borderBottom:"1.5px solid", borderLeft:"1.5px solid"  } },
    { cls:"bottom-8 right-8", style:{ borderBottom:"1.5px solid", borderRight:"1.5px solid" } },
  ];

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="dvyg-loader"
          initial={{ opacity: 1 }}
          exit={{ y: "-100%", transition: { duration: 0.85, ease: [0.76, 0, 0.24, 1] } }}
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden"
          style={{
            background:
              "linear-gradient(148deg, #0F0500 0%, #1E0B00 32%, #0A0300 62%, #170700 100%)",
          }}
        >
          {/* Dot grid */}
          <div className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: "radial-gradient(circle,rgba(242,154,12,0.06) 1px,transparent 1px)",
              backgroundSize: "38px 38px",
            }}
          />

          {/* Amber bloom   top right */}
          <motion.div
            animate={{ scale:[1,1.3,1], opacity:[0.18,0.30,0.18] }}
            transition={{ duration:5, repeat:Infinity, ease:"easeInOut" }}
            className="absolute top-[-22%] right-[-10%] w-[700px] h-[700px] rounded-full pointer-events-none"
            style={{
              background: "radial-gradient(circle,rgba(242,154,12,0.28),rgba(200,80,0,0.07) 52%,transparent 70%)",
              filter: "blur(85px)",
            }}
          />

          {/* Soft orange   left */}
          <motion.div
            animate={{ scale:[1,1.2,1], opacity:[0.09,0.18,0.09] }}
            transition={{ duration:7, repeat:Infinity, ease:"easeInOut", delay:2 }}
            className="absolute top-[18%] left-[-14%] w-[520px] h-[520px] rounded-full pointer-events-none"
            style={{
              background: "radial-gradient(circle,rgba(220,100,0,0.20),transparent 65%)",
              filter: "blur(90px)",
            }}
          />

          {/* Warm glow   bottom */}
          <motion.div
            animate={{ opacity:[0.07,0.14,0.07] }}
            transition={{ duration:6, repeat:Infinity, ease:"easeInOut", delay:1 }}
            className="absolute bottom-[-12%] left-[28%] w-[420px] h-[300px] rounded-full pointer-events-none"
            style={{
              background: "radial-gradient(circle,rgba(242,154,12,0.14),transparent 65%)",
              filter: "blur(80px)",
            }}
          />

          {/* Gold accent lines top + bottom */}
          <div className="absolute top-0 left-0 right-0 h-[2px]"
            style={{ background:"linear-gradient(90deg,transparent,#F29A0C 30%,#FFD166 50%,#F29A0C 70%,transparent)" }}/>
          <div className="absolute bottom-0 left-0 right-0 h-[2px]"
            style={{ background:"linear-gradient(90deg,transparent,#E97818 30%,#F29A0C 50%,#E97818 70%,transparent)" }}/>

          {/* Corner brackets */}
          {brackets.map((b, i) => (
            <motion.div key={i}
              initial={{ opacity:0, scale:0.6 }}
              animate={{ opacity:1, scale:1 }}
              transition={{ delay:0.08 + i*0.07, duration:0.5, ease:[0.22,1,0.36,1] }}
              className={`absolute w-[44px] h-[44px] pointer-events-none ${b.cls}`}
              style={{ ...b.style, borderColor:"rgba(242,154,12,0.30)" }}
            />
          ))}

          {/* Particles */}
          <Particles />

          {/* ── MAIN CONTENT ── */}
          <div className="relative z-10 flex flex-col items-center gap-6 select-none">

            {/* Logo + progress ring */}
            <LogoWithRing progress={progress} />

            {/* DIVYYOG letters   stagger drop */}
            <div className="flex items-end" style={{ gap:2, marginTop:-6 }}>
              {"DIVYYOG".split("").map((ch, i) => (
                <motion.span key={i}
                  initial={{ opacity:0, y:30, filter:"blur(8px)" }}
                  animate={{ opacity:1, y:0,  filter:"blur(0px)" }}
                  transition={{ delay:0.4 + i*0.09, duration:0.68, ease:[0.22,1,0.36,1] }}
                  style={{
                    fontFamily:"'Poppins',sans-serif",
                    fontSize:"clamp(40px,5.5vw,54px)",
                    fontWeight:900, lineHeight:1, letterSpacing:"5px",
                    background:"linear-gradient(135deg,#F29A0C 0%,#FFD166 45%,#E97818 100%)",
                    WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text",
                    filter:"drop-shadow(0 0 16px rgba(242,154,12,0.30))",
                  }}
                >
                  {ch}
                </motion.span>
              ))}
            </div>

            {/* FOUNDATION */}
            <motion.p
              initial={{ opacity:0, letterSpacing:"22px" }}
              animate={{ opacity:0.42, letterSpacing:"9px" }}
              transition={{ delay:1.0, duration:1.1, ease:[0.22,1,0.36,1] }}
              style={{
                fontFamily:"'Inter',sans-serif", fontSize:11, fontWeight:600,
                textTransform:"uppercase", color:"#FFFFFF", marginTop:-4,
              }}
            >
              Foundation
            </motion.p>

            {/* Progress bar */}
            <motion.div
              initial={{ opacity:0, scaleX:0 }}
              animate={{ opacity:1, scaleX:1 }}
              transition={{ delay:0.55, duration:0.5, ease:[0.22,1,0.36,1] }}
              className="relative rounded-full overflow-hidden"
              style={{ width:210, height:2, background:"rgba(255,255,255,0.07)", transformOrigin:"left" }}
            >
              <div className="absolute inset-y-0 left-0 rounded-full"
                style={{
                  width:`${progress}%`,
                  background:"linear-gradient(90deg,#E97818,#F29A0C,#FFD166)",
                  boxShadow:"0 0 12px rgba(242,154,12,0.70)",
                  transition:"width 0.05s linear",
                }}
              />
              {/* Shimmer */}
              <div className="absolute inset-y-0 w-[50px] rounded-full"
                style={{
                  background:"linear-gradient(90deg,transparent,rgba(255,255,255,0.30),transparent)",
                  left:`${Math.max(0, progress - 10)}%`,
                  transition:"left 0.05s linear",
                }}
              />
            </motion.div>

            {/* Tagline */}
            <motion.p
              initial={{ opacity:0 }}
              animate={{ opacity:0.28 }}
              transition={{ delay:1.3, duration:0.9 }}
              style={{
                fontFamily:"'Inter',sans-serif", fontSize:11, color:"#FFD166",
                letterSpacing:"2.5px", textTransform:"uppercase", marginTop:-4,
              }}
            >
              Yoga · Wellness · Excellence
            </motion.p>
          </div>

        </motion.div>
      )}
    </AnimatePresence>
  );
}